import { LegalPageLayout } from '@/components/legal/LegalPageLayout'

export default function CookiePolicyPage() {
  return (
    <LegalPageLayout title="Cookie Policy" lastUpdated="November 20, 2025">
      <section>
        <h2>1. Introduction</h2>
        <p>
          This Cookie Policy explains how EcoSemantic (operated by Kimel Tech SpA) uses cookies and
          similar technologies on our website ecosemantic.com.
        </p>
      </section>

      <section>
        <h2>2. What Are Cookies</h2>
        <p>
          Cookies are small text files stored on your device when you visit a website. They help
          websites remember your preferences and improve your experience.
        </p>
      </section>

      <section>
        <h2>3. Cookies We Use</h2>

        <h3>3.1 Essential Cookies (No Consent Required)</h3>
        <p>
          These cookies are necessary for the website to function and cannot be disabled without
          breaking core functionality:
        </p>
        <table>
          <thead>
            <tr>
              <th>Cookie Name</th>
              <th>Purpose</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>auth_token</td>
              <td>Authentication and session management</td>
              <td>Session / 7 days</td>
            </tr>
            <tr>
              <td>csrf_token</td>
              <td>Security protection against cross-site attacks</td>
              <td>Session</td>
            </tr>
          </tbody>
        </table>

        <h3>3.2 Functional Cookies (Requires Consent)</h3>
        <p>
          These cookies enhance functionality but are not essential. You can accept or decline them:
        </p>
        <table>
          <thead>
            <tr>
              <th>Cookie Name</th>
              <th>Purpose</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>ecosemantic-theme</td>
              <td>Stores your dark/light mode preference</td>
              <td>Persistent (1 year)</td>
            </tr>
            <tr>
              <td>ecosemantic-cookie-consent</td>
              <td>Remembers your cookie consent choice</td>
              <td>Session</td>
            </tr>
          </tbody>
        </table>

        <h3>3.3 Analytics Cookies (Requires Consent)</h3>
        <p>
          These cookies help us understand how visitors use our website so we can improve it. All
          analytics data is collected anonymously:
        </p>
        <table>
          <thead>
            <tr>
              <th>Cookie Name</th>
              <th>Provider</th>
              <th>Purpose</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>_ga</td>
              <td>Google Analytics</td>
              <td>Distinguishes unique users anonymously</td>
              <td>2 years</td>
            </tr>
            <tr>
              <td>_ga_*</td>
              <td>Google Analytics</td>
              <td>Maintains session state</td>
              <td>2 years</td>
            </tr>
          </tbody>
        </table>
        <p className="text-sm text-muted-foreground mt-2">
          <strong>Important:</strong> We use Google Analytics with IP anonymization enabled and have
          disabled data sharing with Google for advertising purposes. Analytics data is used solely
          to improve our platform, not for advertising or tracking.
        </p>
      </section>

      <section>
        <h2>4. Cookies We Do NOT Use</h2>
        <p>We deliberately avoid using:</p>
        <ul>
          <li>
            <strong>Marketing/Advertising Cookies</strong> - No ad targeting or remarketing
          </li>
          <li>
            <strong>Social Media Tracking</strong> - No Facebook, Twitter, LinkedIn pixels
          </li>
          <li>
            <strong>Cross-Site Tracking</strong> - No third-party profiling networks
          </li>
          <li>
            <strong>Unnecessary Third-Party Services</strong> - Minimal external dependencies
          </li>
        </ul>
        <p>
          <strong>Our Analytics Approach:</strong> We use Google Analytics only with your consent,
          with IP anonymization enabled, and without sharing data for advertising. This is solely to
          understand how to improve our platform.
        </p>
      </section>

      <section>
        <h2>5. Third-Party Cookies</h2>
        <p>We minimize third-party cookies but use select services for essential functionality:</p>
        <ul>
          <li>
            <strong>Google Analytics</strong> - Anonymous usage statistics (with your consent). IP
            anonymization enabled. No data sharing for advertising. See{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Google Privacy Policy
            </a>
          </li>
          <li>
            <strong>Paddle.com</strong> - Payment processing (only during checkout). See{' '}
            <a
              href="https://www.paddle.com/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Paddle Privacy Policy
            </a>
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          <strong>Your Control:</strong> Analytics cookies are only set with your explicit consent.
          Payment processor cookies are limited to checkout flow only.
        </p>
      </section>

      <section>
        <h2>6. Managing Your Cookie Preferences</h2>

        <h3>6.1 Cookie Banner</h3>
        <p>When you first visit our site, you'll see a cookie banner with three options:</p>
        <ul>
          <li>
            <strong>Accept All</strong> - Essential + functional + analytics cookies
          </li>
          <li>
            <strong>Functional Only</strong> - Essential + functional cookies (no analytics)
          </li>
          <li>
            <strong>Essential Only</strong> - Only essential cookies (minimal functionality)
          </li>
        </ul>

        <h3>6.2 Changing Your Choice</h3>
        <p>To change your cookie preferences after initial choice:</p>
        <ul>
          <li>Clear your browser session storage</li>
          <li>Revisit the site and make a new choice in the banner</li>
          <li>Or manually delete the "ecosemantic-cookie-consent" session storage item</li>
        </ul>

        <h3>6.3 Browser Settings</h3>
        <p>You can also control cookies through your browser settings:</p>
        <ul>
          <li>
            <strong>Chrome:</strong> Settings → Privacy and Security → Cookies
          </li>
          <li>
            <strong>Firefox:</strong> Settings → Privacy & Security → Cookies
          </li>
          <li>
            <strong>Safari:</strong> Preferences → Privacy → Cookies
          </li>
          <li>
            <strong>Edge:</strong> Settings → Cookies and Site Permissions
          </li>
        </ul>
        <p>
          <strong>Note:</strong> Blocking essential cookies will prevent you from logging in and
          using the platform.
        </p>
      </section>

      <section>
        <h2>7. Cookie Lifespan</h2>
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Duration</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Session Cookies</td>
              <td>Until browser closes</td>
              <td>Temporary, deleted when you close browser</td>
            </tr>
            <tr>
              <td>Persistent Cookies</td>
              <td>Up to 1 year</td>
              <td>Remain until expiration or manual deletion</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>8. Data Collected via Cookies</h2>
        <p>Our cookies collect minimal data:</p>
        <ul>
          <li>
            <strong>Essential Cookies:</strong> Session ID, authentication status, security tokens
          </li>
          <li>
            <strong>Functional Cookies:</strong> Theme preference (dark/light), consent status
          </li>
          <li>
            <strong>Analytics Cookies (with consent):</strong> Anonymous page views, session
            duration, general geographic location (country level), device type, browser type
          </li>
        </ul>
        <p>
          <strong>We do NOT collect via analytics:</strong>
        </p>
        <ul>
          <li>Personal identifiable information</li>
          <li>Precise location data (GPS coordinates)</li>
          <li>Individual user tracking across websites</li>
          <li>Data for advertising or remarketing</li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          <strong>IP Anonymization:</strong> Google Analytics anonymizes IP addresses before
          storage, so we never see your full IP address.
        </p>
      </section>

      <section>
        <h2>9. Legal Basis (GDPR)</h2>
        <p>Cookie usage legal basis:</p>
        <ul>
          <li>
            <strong>Essential Cookies:</strong> Legitimate interest (GDPR Article 6(1)(f)) -
            necessary for service provision
          </li>
          <li>
            <strong>Functional Cookies:</strong> Consent (GDPR Article 6(1)(a)) - obtained via
            banner
          </li>
          <li>
            <strong>Analytics Cookies:</strong> Consent (GDPR Article 6(1)(a)) - explicit opt-in
            required via banner
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          We implement "consent mode" for Google Analytics, ensuring no analytics cookies are set
          until you explicitly consent.
        </p>
      </section>

      <section>
        <h2>10. Updates to This Policy</h2>
        <p>
          We may update this Cookie Policy to reflect changes in our practices or for legal reasons.
          Changes will be posted on this page with an updated date.
        </p>
      </section>

      <section>
        <h2>11. Contact Us</h2>
        <p>Questions about our cookie usage?</p>
        <p>
          <strong>Email:</strong> support@ecosemantic.com
          <br />
          <strong>Company:</strong> Kimel Tech SpA, Chile
        </p>
      </section>

      <div className="mt-12 p-6 bg-muted rounded-lg">
        <h3 className="text-lg font-semibold mb-2">Summary: Our Cookie Philosophy</h3>
        <p className="text-sm">
          We use the absolute minimum cookies necessary. Essential cookies keep you logged in
          safely. Functional cookies (with your consent) remember your theme preference. Analytics
          cookies (with your consent) help us understand anonymous usage patterns to improve our
          platform - with IP anonymization and no advertising use. You have full control over all
          non-essential cookies.
        </p>
      </div>
    </LegalPageLayout>
  )
}
